import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/UI/Button/Button';
import Input from '../../components/UI/Input/Input';
import TextArea from '../../components/UI/TextArea/TextArea';
import PlacementSelector from '../../components/UI/PlacementSelector/PlacementSelector';
import { toast } from 'react-toastify';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import { uploadFile } from '../../services/uploadService';
import { compressImage } from '../../utils/imageCompression';
import FileUploadField from '../../components/UI/FileUploadField/FileUploadField';
import styleService from '../../services/styleService';
import './ManageArtworksPage.v2.css';

const ManageArtworksPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [artworks, setArtworks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingArtwork, setEditingArtwork] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const [formData, setFormData] = useState({
        images: [],
        title: '',
        description: '',
        category: '',
        placements: [],
        pricing: {
            basePrice: 0,
            withColor: 0,
            withoutColor: 0,
            isPriceRange: false,
            minPrice: 0,
            maxPrice: 0
        }
    });

    const [isUploading, setIsUploading] = useState(false);
    const [availableStyles, setAvailableStyles] = useState([]);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, artworkId: null });

    const showAlert = (title, message, type = 'info', onAfterClose = null) => {
        if (type === 'error') toast.error(message || title);
        else if (type === 'success') toast.success(message || title);
        else if (type === 'warning') toast.warn(message || title);
        else toast.info(message || title);

        if (onAfterClose) setTimeout(onAfterClose, 3000);
    };

    const { token } = useAuth();

    const fetchArtworks = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get(`/artworks/my?page=${currentPage}&limit=12`);
            setArtworks(res.data.artworks);
            setTotalPages(res.data.totalPages);
        } catch (err) {
            console.error('Error fetching artworks:', err);
            showAlert(t('error'), t('artwork_error'), 'error');
        } finally {
            setLoading(false);
        }
    }, [currentPage, t]);

    useEffect(() => {
        const fetchStyles = async () => {
            try {
                const styles = await styleService.getAllStyles();
                if (Array.isArray(styles)) {
                    setAvailableStyles(styles);
                } else {
                    console.error('Styles is not an array:', styles);
                }
            } catch (err) {
                console.error('Error fetching styles:', err);
            }
        };
        fetchStyles();
    }, []);

    useEffect(() => {
        fetchArtworks();
    }, [fetchArtworks]);

    const resetForm = () => {
        setFormData({
            images: [],
            title: '',
            description: '',
            category: '',
            placements: [],
            pricing: {
                basePrice: 0,
                withColor: 0,
                withoutColor: 0,
                isPriceRange: false,
                minPrice: 0,
                maxPrice: 0
            }
        });
        setEditingArtwork(null);
    };

    const handleOpenModal = (artwork = null) => {
        if (artwork) {
            setEditingArtwork(artwork);
            setFormData({
                images: artwork.images || (artwork.imageUrl ? [artwork.imageUrl] : []),
                title: artwork.title || '',
                description: artwork.description || '',
                category: artwork.category,
                placements: artwork.placements || [],
                pricing: artwork.pricing || {
                    basePrice: 0,
                    withColor: 0,
                    withoutColor: 0,
                    isPriceRange: false,
                    minPrice: 0,
                    maxPrice: 0
                }
            });
        } else {
            resetForm();
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        resetForm();
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        const currentImagesCount = formData.images.length;
        const availableSlots = 5 - currentImagesCount;

        if (availableSlots <= 0) {
            showAlert(t('error'), t('max_artwork_images_reached', 'Máximo de 5 imagens por arte atingido.'), 'warning');
            return;
        }

        // Limit selected files to available slots
        const filesToUpload = files.slice(0, availableSlots);

        if (files.length > availableSlots) {
            showAlert(t('info'), t('limit_reached_some_files_ignored', { count: availableSlots }), 'info');
        }

        setIsUploading(true);
        try {
            for (const file of filesToUpload) {
                try {
                    const compressed = await compressImage(file);
                    const url = await uploadFile(compressed, token, 'artworks');
                    setFormData(prev => ({
                        ...prev,
                        images: [...prev.images, url]
                    }));
                } catch (fileErr) {
                    console.error(`Error uploading file ${file.name}:`, fileErr);
                    // Show error but continue with other files
                    showAlert(t('error'), `${file.name}: ${fileErr.message || t('artwork_error')}`, 'error');
                }
            }
        } catch (err) {
            console.error('Core upload process error:', err);
        } finally {
            setIsUploading(false);
        }
    };

    const handleRemoveImage = (index) => {
        const newImages = formData.images.filter((_, i) => i !== index);
        setFormData({ ...formData, images: newImages });
    };

    const handlePricingChange = (field, value) => {
        setFormData({
            ...formData,
            pricing: {
                ...formData.pricing,
                [field]: value
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.images.length || !formData.category) {
            showAlert(t('error'), t('required_field'), 'warning');
            return;
        }

        try {
            if (editingArtwork) {
                await api.put(`/artworks/${editingArtwork.id}`, formData);
                showAlert(t('success'), t('artwork_updated_success'), 'success');
            } else {
                await api.post(`/artworks`, formData);
                showAlert(t('success'), t('artwork_created_success'), 'success');
            }
            handleCloseModal();
            fetchArtworks();
        } catch (err) {
            console.error('Error saving artwork:', err);
            showAlert(t('error'), t('artwork_error'), 'error');
        }
    };

    const handleDelete = (artworkId) => {
        setConfirmModal({
            isOpen: true,
            artworkId
        });
    };

    const confirmDelete = async () => {
        if (!confirmModal.artworkId) return;
        try {
            await api.delete(`/artworks/${confirmModal.artworkId}`);
            toast.success(t('artwork_deleted_success'));
            fetchArtworks();
        } catch (err) {
            console.error('Error deleting artwork:', err);
            toast.error(t('artwork_error'));
        }
        setConfirmModal({ isOpen: false, artworkId: null });
    };

    if (loading && artworks.length === 0) {
        return (
            <div style={{ minHeight: '100vh', background: '#0a0a0a', color: '#fff', paddingTop: '80px' }}>
                <div style={{ textAlign: 'center', padding: '100px 20px' }}>{t('loading')}</div>
            </div>
        );
    }

    return (
        <div className="manage-artworks-page">
            <div className="manage-artworks-content">
                <div className="page-header">
                    <h1>{t('manage_artworks')}</h1>
                    <Button variant="primary" onClick={() => handleOpenModal()}>
                        + {t('add_new_artwork')}
                    </Button>
                </div>

                {artworks.length === 0 ? (
                    <div className="empty-state">
                        <h3>{t('no_artworks')}</h3>
                        <p>{t('no_artworks_description')}</p>
                        <Button variant="primary" onClick={() => handleOpenModal()}>
                            {t('add_new_artwork')}
                        </Button>
                    </div>
                ) : (
                    <>
                        <div className="artworks-grid">
                            {artworks.map((artwork) => (
                                <div className="artwork-item" key={artwork.id}>
                                    <div
                                        className="artwork-item-image"
                                        onClick={() => navigate(`/artworks/${artwork.id}`)}
                                        style={{ cursor: 'pointer', position: 'relative', display: 'block' }}
                                    >
                                        <img src={artwork.images?.[0] || artwork.imageUrl} alt={artwork.title || t('artwork_alt')} />
                                        <div className="view-overlay">
                                            <span>👁️ {t('view')}</span>
                                        </div>
                                    </div>
                                    <div className="artwork-item-info">
                                        <h4>{artwork.title || t('artwork_title')}</h4>
                                        <span className="category-badge">
                                            {t(`category_${artwork.category.toLowerCase()}`)}
                                        </span>
                                    </div>
                                    <div className="artwork-item-actions">
                                        <button
                                            className="edit-btn"
                                            onClick={() => handleOpenModal(artwork)}
                                            title={t('edit_artwork')}
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                            </svg>
                                            <span className="btn-text">{t('edit_artwork')}</span>
                                        </button>
                                        <button
                                            className="delete-btn"
                                            onClick={() => handleDelete(artwork.id)}
                                            title={t('delete_artwork')}
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="3 6 5 6 21 6"></polyline>
                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                                <line x1="14" y1="11" x2="14" y2="17"></line>
                                            </svg>
                                            <span className="btn-text">{t('delete_artwork')}</span>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {totalPages > 1 && (
                            <div className="pagination">
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                >
                                    {t('previous_page')}
                                </button>
                                <span>{t('page_of', { current: currentPage, total: totalPages })}</span>
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                >
                                    {t('next_page')}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingArtwork ? t('edit_artwork') : t('add_new_artwork')}</h2>
                            <button className="close-modal-btn" onClick={handleCloseModal}>×</button>
                        </div>

                        <form onSubmit={handleSubmit} className="artwork-form">
                            <Input
                                label={t('artwork_title')}
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                placeholder={t('artwork_title_placeholder')}
                            />

                            <div className="form-group">
                                <label>{t('artwork_images')} *</label>
                                <div className="artwork-upload-grid">
                                    {formData.images.map((img, idx) => (
                                        <div key={idx} className="artwork-preview-item">
                                            <FileUploadField
                                                value={img}
                                                uploading={false}
                                                onChange={() => { }}
                                                type="square"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveImage(idx)}
                                                className="remove-img-btn"
                                            >
                                                ×
                                            </button>
                                            {idx === 0 && <span className="main-tag">{t('main_badge')}</span>}
                                        </div>
                                    ))}
                                    {formData.images.length < 5 && (
                                        <FileUploadField
                                            onChange={handleFileChange}
                                            uploading={isUploading}
                                            type="square"
                                            hint={t('max_5_images', 'Máximo 5 imagens')}
                                            multiple={true}
                                        />
                                    )}
                                </div>
                            </div>

                            <TextArea
                                label={t('artwork_description')}
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder={t('artwork_description_placeholder')}
                                rows={4}
                            />

                            <div className="form-group">
                                <label>{t('artwork_category')} *</label>
                                <select
                                    name="category"
                                    value={formData.category}
                                    onChange={handleChange}
                                    required
                                    className="category-select"
                                >
                                    <option value="">{t('select_category')}</option>
                                    {availableStyles.map(style => (
                                        <option key={style.id} value={style.name}>
                                            {t(`style_${style.name.toLowerCase().replace(/\s+/g, '_')}`) || style.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <PlacementSelector
                                placements={formData.placements}
                                onChange={(placements) => setFormData({ ...formData, placements })}
                            />

                            <div className="pricing-section-container">
                                <h3>{t('pricing_section')}</h3>

                                <div className="pricing-type-selector">
                                    <button
                                        type="button"
                                        className={`type-btn ${!formData.pricing.isPriceRange ? 'active' : ''}`}
                                        onClick={() => handlePricingChange('isPriceRange', false)}
                                    >
                                        {t('fixed_options', 'Fixed Options')}
                                    </button>
                                    <button
                                        type="button"
                                        className={`type-btn ${formData.pricing.isPriceRange ? 'active' : ''}`}
                                        onClick={() => handlePricingChange('isPriceRange', true)}
                                    >
                                        {t('price_range', 'Price Range')}
                                    </button>
                                </div>

                                <div className="pricing-inputs-grid">
                                    {formData.pricing.isPriceRange ? (
                                        <>
                                            <Input
                                                name="minPrice"
                                                label={t('min_price')}
                                                type="number"
                                                value={formData.pricing.minPrice}
                                                onChange={(e) => handlePricingChange('minPrice', parseFloat(e.target.value) || 0)}
                                                placeholder="0"
                                            />
                                            <Input
                                                name="maxPrice"
                                                label={t('max_price')}
                                                type="number"
                                                value={formData.pricing.maxPrice}
                                                onChange={(e) => handlePricingChange('maxPrice', parseFloat(e.target.value) || 0)}
                                                placeholder="0"
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <Input
                                                name="withColor"
                                                label={t('with_color')}
                                                type="number"
                                                value={formData.pricing.withColor}
                                                onChange={(e) => handlePricingChange('withColor', parseFloat(e.target.value) || 0)}
                                                placeholder="0"
                                            />
                                            <Input
                                                name="withoutColor"
                                                label={t('without_color')}
                                                type="number"
                                                value={formData.pricing.withoutColor}
                                                onChange={(e) => handlePricingChange('withoutColor', parseFloat(e.target.value) || 0)}
                                                placeholder="0"
                                            />
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="modal-actions">
                                <Button type="submit" variant="primary" size="large">
                                    {t('save_artwork')}
                                </Button>
                                <Button type="button" variant="outline" size="large" onClick={handleCloseModal}>
                                    {t('cancel')}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmationModal
                isOpen={confirmModal.isOpen}
                title={t('confirm_delete_artwork_title', 'Delete Artwork')}
                message={t('confirm_delete_artwork')}
                onConfirm={confirmDelete}
                onCancel={() => setConfirmModal({ isOpen: false, artworkId: null })}
            />
        </div>
    );
};

export default ManageArtworksPage;
